<script setup>
import { RouterLink, RouterView } from 'vue-router'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useSensorStore } from './stores/sensorStore.js';
import { useUiStore } from './stores/uiStore'
import NotificationBanner from './components/NotificationBanner.vue';
import DialogBox from './components/DialogBox.vue';
import router from './router';

const store = useSensorStore();
const uiStore = useUiStore();

const closeNavButtonState = ref(false);
const appVersion = ref(__APP_VERSION__);
const screenWidth = ref(window.innerWidth);

const isDeviceConnected = computed(() => {
  return store.sensorState.connected == 'connected';
});

const isMobile = computed(() => {
  return screenWidth.value < 690;
});

const dialogBoxClass = computed(() => {
  if (isMobile.value || closeNavButtonState.value) {
    return 'dialog-box-big';
  }

  return 'dialog-box-small';
});

const contentClass = computed(() => {
  if (isMobile.value) {
    return 'content-mobile';
  }

  return closeNavButtonState.value ? 'content-big' : 'content-small';
});

const desktopRouteLabelClass = computed(() => {
  return closeNavButtonState.value ? 'hide-on-small' : '';
});

const showMeasureRoute = computed(() => {
  return isDeviceConnected.value
    && uiStore.uiState.firmwareUpdateInitiated == false
    && uiStore.uiState.currentlyGettingData == false;
});

const showDataRoute = computed(() => {
  return isDeviceConnected.value
    && store.sensorState.deviceMode == 'idle'
    && uiStore.uiState.firmwareUpdateInitiated == false;
});

const showSettingsRoute = computed(() => {
  return isDeviceConnected.value
    && store.sensorState.deviceMode == 'idle'
    && uiStore.uiState.firmwareUpdateInitiated == false
    && uiStore.uiState.currentlyGettingData == false;
});

function updateWidth() {
  screenWidth.value = window.innerWidth;
  if (isMobile.value) {
    closeNavButtonState.value = false;
  }
}

const toggleDesktopNavigation = () => {
  if (isMobile.value) {
    return;
  }

  closeNavButtonState.value = !closeNavButtonState.value;
};

onMounted(() => {
  window.addEventListener('resize', updateWidth);
  if (isMobile.value) {
    closeNavButtonState.value = false;
  }
  router.push('/connect');
});

watch(
  () => store.sensorState.connected,
  (connectedState) => {
    if (connectedState !== 'disconnected') {
      return;
    }

    if (router.currentRoute.value.path !== '/connect') {
      router.push('/connect');
    }
  },
);

onUnmounted(() => {
  window.removeEventListener('resize', updateWidth);
});
</script>

<template>
 <div class="applayout">
   <aside v-if="!isMobile" :class="closeNavButtonState ? 'sidebar sidebar--narrow' : 'sidebar sidebar--wide'">
    <div class="logoname">
      <img src="@/assets/gammalogo.png" alt="">
      <h3>SOLACE-W</h3>
    </div>
    
    <RouterLink
      v-if="uiStore.uiState.firmwareUpdateInitiated == false"
      class="routename"
      active-class="route-active"
      to="/connect"
    >
      <div class="routeicon connect-icon stay-on-big"></div>
      <div :class="desktopRouteLabelClass">Connect</div>
    </RouterLink>

    <RouterLink
      v-if="showMeasureRoute"
      class="routename"
      active-class="route-active"
      to="/measure"
    >
      <div class="routeicon measure-icon stay-on-big"></div>
      <div :class="desktopRouteLabelClass">Measure</div>
    </RouterLink>
    
    <RouterLink
      v-if="showDataRoute"
      class="routename"
      active-class="route-active"
      to="/data"
    >
      <div class="routeicon store-icon stay-on-big"></div>
      <div :class="desktopRouteLabelClass">Data</div>
    </RouterLink>

    <RouterLink
      v-if="showSettingsRoute"
      class="routename"
      active-class="route-active"
      to="/settings"
    >
      <div class="routeicon settings-icon stay-on-big"></div>
      <div :class="desktopRouteLabelClass">Settings</div>
    </RouterLink>
   </aside>

   <div class="maincontent">
      <div class="topbar">
        <button
          v-if="!isMobile"
          @click="toggleDesktopNavigation"
          class="closeNavbtn"
          :style="{'margin-left': closeNavButtonState ? '50px' : '250px'}"
        >&#9776;</button>
        <div v-else class="mobile-top-spacer"></div>
        <h2>PhotoSpec Labs</h2>
        <div class="topbar-version">
          <p>{{appVersion}}</p>
        </div>
      </div>

      <Transition name="bounce">
        <div v-if="uiStore.uiState.dialogBox.show" :class="dialogBoxClass">
          <DialogBox
            :showProp="uiStore.uiState.dialogBox.show"
            :textProp="uiStore.uiState.dialogBox.message"
            @confirmed="{uiStore.updateDialogBox(false, null, null); uiStore.uiState.dialogBox.confirmationResolver('confirmed')}"
            @close="{uiStore.updateDialogBox(false, null, null); uiStore.uiState.dialogBox.confirmationResolver('cancelled')}"
          ></DialogBox>
        </div>
      </Transition>
    
      <div :class="contentClass">
        <RouterView/>
      </div>
   </div>

   <nav v-if="isMobile" class="bottom-nav">
    <RouterLink
      v-if="uiStore.uiState.firmwareUpdateInitiated == false"
      class="bottom-nav-item"
      active-class="bottom-nav-item--active"
      to="/connect"
    >
      <div class="routeicon connect-icon"></div>
      <span>Connect</span>
    </RouterLink>

    <RouterLink
      v-if="showMeasureRoute"
      class="bottom-nav-item"
      active-class="bottom-nav-item--active"
      to="/measure"
    >
      <div class="routeicon measure-icon"></div>
      <span>Measure</span>
    </RouterLink>

    <RouterLink
      v-if="showDataRoute"
      class="bottom-nav-item"
      active-class="bottom-nav-item--active"
      to="/data"
    >
      <div class="routeicon store-icon"></div>
      <span>Data</span>
    </RouterLink>

    <RouterLink
      v-if="showSettingsRoute"
      class="bottom-nav-item"
      active-class="bottom-nav-item--active"
      to="/settings"
    >
      <div class="routeicon settings-icon"></div>
      <span>Settings</span>
    </RouterLink>
   </nav>
   
   <NotificationBanner
     :visible="uiStore.uiState.showNotificationBanner"
     @close="uiStore.updateShowNotificationBanner(false)"
     class="notification-banner"
     :type="uiStore.uiState.notificationBannerType"
     :text="uiStore.uiState.notificationBannerText"
   />
 </div>
</template>

<style scoped>
:root {
  --sidebarCollapsedWidth: 50px;
  --myapp-color: #ffc107;
}

.applayout{
  display: flex;
  flex-direction: column;
}

.sidebar {
  top: 10px;
  left: 12px;
  bottom: 10px;
  height: auto;
  position: fixed;
  z-index: 20;
  overflow-x: hidden;
  transition: 0.3s;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  background: rgba(15, 20, 40, 0.62);
  overflow: hidden;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 26px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.22);
}

.sidebar::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, rgba(34, 34, 34, 0.2), transparent, transparent, rgba(255, 255, 255, 0.2));
  transform: rotate(-45deg);
  z-index: -1;
}

.sidebar::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.2), transparent);
  transform: translate(-50%, -50%);
  z-index: -2;
}

.sidebar--narrow{
  width: 70px;
}

.sidebar--wide{
  width: 220px;
}

.sidebar img{
  width: 34px;
  height: auto;
  padding-right: 10px;
}

.sidebar a {
  padding: 0 16px;
  text-decoration: none;
  font-size: 1rem;
  transition: 0.3s;
  color: #dfdfdf;
}

.sidebar .logoname {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 4px 8px 10px 8px;
  margin-bottom: 10px;
}

.sidebar a:hover {
  color: #f1f1f1;
}

.closeNavbtn{
  z-index: 21;
  background-color: transparent;
  border: none;
  font-weight: bold;
  color: white;
  font-size: 28px;
  align-self: center;
  margin-left: 50px;
  transition: margin-left 0.3s;
  padding: 0 8px 0 0;
  min-width: 44px;
  min-height: 44px;
}

.closeNavbtn:hover{
  color: white ;
}

.closeNavbtn:active {
  animation: glow 3s infinite;
}

.mobile-top-spacer {
  width: 12px;
  flex: 0 0 12px;
}

.topbar{
  position: fixed;
  top: 10px;
  left: 12px;
  right: 12px;
  z-index: 10;
  width: auto;
  background: rgba(15, 20, 40, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.22);
  border-radius: 22px;
  height: 64px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  padding: 0 16px;
  box-sizing: border-box;
}

.topbar h2 {
  color: white;
  align-self: center;
  margin-left: 10px;
  width: 100%;
  font-size: clamp(0.95rem, 1rem + 0.3vw, 1.25rem);
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.02em;
}

.topbar-version {
  display: flex; 
  justify-content:flex-end; 
  min-width: fit-content;
}

.topbar p {
  color: white;
  align-self: center;
  margin-right: 0;
  opacity: 0.92;
}

.maincontent{
  display: flex;
  flex-direction: column;
}

.content-small{
  position: fixed;
  width: 100%;
  transition: 0.3s;
  height: 100%;
  padding-left: 250px;
  padding-top: 100px;
  padding-bottom: 100px;
  overflow-x: hidden;
  overflow-y: auto;
}

.content-small::-webkit-scrollbar {
  width: 10px;
}

.content-small::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.1);
}

.content-small::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.3);
  border-radius: 20px;
}

.content-small::-webkit-scrollbar-thumb:hover {
  background: rgba(255,255,255,0.4);
}

.content-big{
  position: fixed;
  width: 100%;
  transition: 0.3s;
  height: 100%;
  padding-left: 50px;
  padding-top: 100px;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: 100px;
}

.content-big::-webkit-scrollbar {
  width: 10px;
}

.content-big::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.1);
}

.content-big::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.3);
  border-radius: 20px;
}

.content-big::-webkit-scrollbar-thumb:hover {
  background: rgba(255,255,255,0.4);
}

.content-mobile{
  position: fixed;
  width: 100%;
  height: 100%;
  padding: 86px 14px 108px 14px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}

.content-mobile::-webkit-scrollbar {
  width: 8px;
}

.content-mobile::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.1);
}

.content-mobile::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.3);
  border-radius: 20px;
}

.routename{
  display: flex;
  align-items: center;
  overflow:visible;
  margin-top: 8px;
  transition: background-color 0.1s ease-in-out;
  border-radius: 18px;
  height: 56px;
  color: white;
  gap: 12px;
}

.routename:hover{
  background-color: rgba(247, 247, 247, 0.1);
}

.route-active {
  transition: background-color 0.1s ease-in-out;
  border-radius: 18px;
  background-color: rgba(255, 255, 255, 0.12);
}

.routeicon {
  background-repeat: no-repeat;
  background-size: contain;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
}

.routename .routeicon {
  margin-right: 2px;
  margin-left: 0;
}

.connect-icon {
  background-image: url('@/assets/wifi-logo-svgrepo-com.svg');
}

.measure-icon {
  background-image: url('@/assets/lightbulb.svg');
}

.store-icon {
  background-image: url('@/assets/storage.svg');
}

.settings-icon {
  background-image: url('@/assets/setting_icon.svg');
}

.logoname {
  white-space: nowrap;
  overflow: visible;
  text-overflow: ellipsis;
  color: white;
}

.logoname h3 {
  margin: 0;
  font-size: 1rem;
  letter-spacing: -0.01em;
  font-weight: 600;
}

.sidebar--narrow .logoname h3 {
  display: none;
}

.sidebar--narrow .logoname {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.sidebar--narrow a {
  padding: 0;
}

.sidebar--narrow .routename {
  justify-content: center;
  gap: 0;
}

.sidebar--narrow .routename .routeicon {
  margin-right: 0;
}

.hide-on-small {
  display: none;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.stay-on-big{
  display: flex;
  align-items: center;
  vertical-align: middle;
}

.bottom-nav {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: 12px;
  z-index: 30;
  display: flex;
  justify-content: space-around;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 22px;
  background: rgba(15, 20, 40, 0.72);
  backdrop-filter: blur(18px);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.bottom-nav-item {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.72);
  text-decoration: none;
  font-size: 0.68rem;
  line-height: 1;
  padding: 7px 4px;
  border-radius: 14px;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.bottom-nav-item span {
  white-space: nowrap;
}

.bottom-nav-item--active {
  background: rgba(255, 255, 255, 0.12);
  color: white;
  transform: translateY(-1px);
}

.notification-banner{
  position: fixed;
  bottom: 10px;
  right: 10px;
  z-index: 1000; 
  margin-left: 250px;
  margin-right: 30px;
  width: 300px;
  animation: fade-in 1s ease-in;
}

.dialog-box-small{
  position: fixed;
  z-index: 1000;
  margin-top: 10%;
  width: 80%;
  height: 50%;
  padding-left: 250px ;
  justify-self: center;
  align-self: center;
}

.dialog-box-big{
  position: fixed;
  z-index: 1000;
  margin-top: 10%;
  width: 80%;
  height: 50%;
  padding-left: 50px ;
  justify-self: center;
  align-self: center;
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes glow {
  0% {
    text-shadow: 0 0 10px #fff, 0 0 20px #fff, 0 0 30px #fff;
  }
  50% {
    text-shadow: 0 0 20px #fff, 0 0 30px #fff, 0 0 40px #ff00f0;
  }
  100% {
    text-shadow: 0 0 10px #fff, 0 0 20px #fff, 0 0 30px #fff;
  }
}

.fade-enter-active,
.bounce-enter-active {
  animation: bounce-in 0.5s;
}

.bounce-leave-active {
  animation: bounce-in 0.5s reverse;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@media (max-width: 690px) {
  .notification-banner {
    left: 12px;
    right: 12px;
    width: auto;
    margin: 0;
    bottom: 92px;
  }

  .dialog-box-big,
  .dialog-box-small {
    width: calc(100% - 24px);
    padding-left: 0;
    left: 12px;
    right: 12px;
    margin-top: 90px;
  }

  .topbar {
    top: 12px;
    left: 12px;
    right: 12px;
    height: 56px;
    padding: 0 14px;
  }

  .topbar h2 {
    margin-left: 0;
    font-size: 0.95rem;
  }

  .topbar p {
    font-size: 0.92rem;
  }

  .bottom-nav {
    bottom: 10px;
    left: 10px;
    right: 10px;
    border-radius: 20px;
  }

  .bottom-nav .routeicon {
    width: 22px;
    height: 22px;
    flex-basis: 22px;
  }
}
</style>
